import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { exportService } from '../services/exportService';
import { SensorReading } from '../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

export type ComparisonMetric =
  | 'temperature'
  | 'soil_moisture'
  | 'electrical_conductivity'
  | 'leaf_temperature'
  | 'leaf_moisture'
  | 'ph_value'
  | 'ph_temperature'
  | 'ec_temperature'
  | 'temp_ds18b20'
  | 'par'
  | 'battery_voltage';

interface SensorReadingsMap {
  [sensorId: number]: SensorReading[];
}

interface ComparisonChartProps {
  sensorIds: number[];
  sensorNames: { [key: number]: string };
}

// Palette of distinct colours — one per sensor
const SENSOR_COLORS = [
  { border: 'rgb(59, 130, 246)',  background: 'rgba(59, 130, 246, 0.5)'  }, // blue
  { border: 'rgb(239, 68, 68)',   background: 'rgba(239, 68, 68, 0.5)'   }, // red
  { border: 'rgb(34, 197, 94)',   background: 'rgba(34, 197, 94, 0.5)'   }, // green
  { border: 'rgb(249, 115, 22)',  background: 'rgba(249, 115, 22, 0.5)'  }, // orange
  { border: 'rgb(168, 85, 247)',  background: 'rgba(168, 85, 247, 0.5)'  }, // purple
  { border: 'rgb(20, 184, 166)',  background: 'rgba(20, 184, 166, 0.5)'  }, // teal
  { border: 'rgb(236, 72, 153)', background: 'rgba(236, 72, 153, 0.5)'  }, // pink
  { border: 'rgb(251, 191, 36)',  background: 'rgba(251, 191, 36, 0.5)'  }, // yellow
];

const METRIC_LABELS: Record<ComparisonMetric, string> = {
  temperature: 'Temperature (°C)',
  soil_moisture: 'Soil Moisture (%)',
  electrical_conductivity: 'EC (mS/cm)',
  leaf_temperature: 'Leaf Temperature (°C)',
  leaf_moisture: 'Leaf Moisture (%)',
  ph_value: 'pH',
  ph_temperature: 'pH Probe Temperature (°C)',
  ec_temperature: 'EC Probe Temperature (°C)',
  temp_ds18b20: 'External DS18B20 Temperature (°C)',
  par: 'PAR (µmol/m²/s)',
  battery_voltage: 'Battery (V)',
};

function getMetricValue(reading: SensorReading, metric: ComparisonMetric): number | null {
  switch (metric) {
    case 'temperature':             return reading.temperature ?? null;
    case 'soil_moisture':           return reading.soil_moisture ?? null;
    case 'electrical_conductivity': return reading.electrical_conductivity != null ? reading.electrical_conductivity / 1000 : null;
    case 'leaf_temperature':        return reading.leaf_temperature ?? null;
    case 'leaf_moisture':           return reading.leaf_moisture ?? null;
    case 'ph_value':                return reading.ph_value ?? null;
    case 'ph_temperature':          return reading.ph_temperature ?? null;
    case 'ec_temperature':          return reading.ec_temperature ?? null;
    case 'temp_ds18b20':            return reading.temp_ds18b20 ?? null;
    case 'par':                      return reading.par ?? null;
    case 'battery_voltage':         return reading.battery_voltage ?? null;
    default:                        return null;
  }
}

const ComparisonChart: React.FC<ComparisonChartProps> = ({ sensorIds, sensorNames }) => {
  const { t } = useTranslation();
  const [allReadings, setAllReadings] = useState<SensorReadingsMap>({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [sensorId: number]: string }>({});

  const [metric, setMetric] = useState<ComparisonMetric>('temperature');
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingInProgressRef = useRef(false);

  // ── helpers ──────────────────────────────────────────────────────────────

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    if (dateRangePreset === 'custom' && startDate && endDate) {
      return { start: new Date(startDate), end: new Date(endDate) };
    }

    switch (dateRangePreset) {
      case '1d':  start.setDate(start.getDate() - 1);  break;
      case '7d':  start.setDate(start.getDate() - 7);  break;
      case '30d': start.setDate(start.getDate() - 30); break;
      case '90d': start.setDate(start.getDate() - 90); break;
    }
    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  const getTimeUnit = useCallback(() => {
    const { start, end } = getDateRange();
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1)  return 'hour';
    if (diffDays <= 60) return 'day';
    return 'week';
  }, [getDateRange]);

  // ── data loading ─────────────────────────────────────────────────────────

  const loadReadings = useCallback(async (isPolling = false) => {
    if (sensorIds.length === 0) return;
    if (isPolling && isPollingInProgressRef.current) return;

    if (isPolling) {
      isPollingInProgressRef.current = true;
    } else {
      setLoading(true);
    }

    const { start, end } = getDateRange();
    const newReadings: SensorReadingsMap = {};
    const newErrors: { [sensorId: number]: string } = {};

    await Promise.all(
      sensorIds.map(async (id) => {
        try {
          const data = await sensorService.getReadings(id, {
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            limit: 5000,
          });
          newReadings[id] = data.sort(
            (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        } catch {
          newErrors[id] = `Failed to load readings for sensor ${sensorNames[id] ?? id}`;
        }
      })
    );

    setAllReadings(newReadings);
    setErrors(newErrors);

    if (isPolling) {
      isPollingInProgressRef.current = false;
    } else {
      setLoading(false);
    }
  }, [sensorIds, getDateRange, sensorNames]);

  // Initial load + reload when inputs change
  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  // Polling every 30 s
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => loadReadings(true), 30000);
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [loadReadings]);

  // ── chart data ────────────────────────────────────────────────────────────

  // Collect all unique timestamps across all sensors and sort them
  const allTimestamps = Array.from(
    new Set(
      Object.values(allReadings)
        .flat()
        .map((r) => new Date(r.timestamp).getTime())
    )
  ).sort((a, b) => a - b);

  const chartData = {
    labels: allTimestamps.map((t) => new Date(t)),
    datasets: sensorIds.map((id, idx) => {
      const readings = allReadings[id] ?? [];
      const color = SENSOR_COLORS[idx % SENSOR_COLORS.length];
      const totalPoints = readings.length;

      // Map each global timestamp to the matching reading value (or null)
      const timestampToValue: Map<number, number | null> = new Map();
      readings.forEach((r) => {
        timestampToValue.set(new Date(r.timestamp).getTime(), getMetricValue(r, metric));
      });

      return {
        label: sensorNames[id] ?? `Sensor ${id}`,
        data: allTimestamps.map((t) => timestampToValue.get(t) ?? null),
        borderColor: color.border,
        backgroundColor: color.background,
        borderWidth: 2,
        pointRadius: totalPoints > 100 ? 0 : 3,
        tension: 0.1,
        spanGaps: true,
      };
    }),
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: `Sensor Comparison — ${METRIC_LABELS[metric]}`,
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length > 0 && items[0].parsed.x !== null && items[0].parsed.x !== undefined) {
              return new Date(items[0].parsed.x).toLocaleString();
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: getTimeUnit(),
          displayFormats: { hour: 'HH:mm', day: 'MMM d', week: 'MMM d' },
        },
        title: { display: true, text: 'Time' },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: { display: true, text: METRIC_LABELS[metric] },
      },
    },
  };

  // ── preset buttons ────────────────────────────────────────────────────────

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) setDateRangePreset('custom');
  };

  const totalReadings = Object.values(allReadings).reduce((s, r) => s + r.length, 0);
  const hasData = totalReadings > 0;
  const errorMessages = Object.values(errors);

  // ── Detect available metrics from loaded readings ──────────────────────────
  const availableMetrics = React.useMemo(() => {
    const metrics = new Set<ComparisonMetric>();
    
    // Check all readings across all sensors to see which fields have data
    Object.values(allReadings).forEach(readings => {
      readings.forEach((reading: SensorReading) => {
        if (reading.temperature !== null && reading.temperature !== undefined) {
          metrics.add('temperature');
        }
        if (reading.soil_moisture !== null && reading.soil_moisture !== undefined) {
          metrics.add('soil_moisture');
        }
        if (reading.electrical_conductivity !== null && reading.electrical_conductivity !== undefined) {
          metrics.add('electrical_conductivity');
        }
        if (reading.leaf_temperature !== null && reading.leaf_temperature !== undefined) {
          metrics.add('leaf_temperature');
        }
        if (reading.leaf_moisture !== null && reading.leaf_moisture !== undefined) {
          metrics.add('leaf_moisture');
        }
        if (reading.battery_voltage !== null && reading.battery_voltage !== undefined) {
          metrics.add('battery_voltage');
        }
        if (reading.ph_value != null) {
          metrics.add('ph_value');
        }
        if (reading.ph_temperature != null) {
          metrics.add('ph_temperature');
        }
        if (reading.ec_temperature != null) {
          metrics.add('ec_temperature');
        }
        if (reading.temp_ds18b20 != null) {
          metrics.add('temp_ds18b20');
        }
        if (reading.par != null) {
          metrics.add('par');
        }
      });
    });
    
    return Array.from(metrics);
  }, [allReadings]);

  // Auto-select first available metric if current metric is not available
  React.useEffect(() => {
    if (availableMetrics.length > 0 && !availableMetrics.includes(metric)) {
      setMetric(availableMetrics[0]);
    }
  }, [availableMetrics, metric]);

  // ── export handler ────────────────────────────────────────────────────────
  
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { start, end } = getDateRange();
      
      await exportService.exportComparisonData({
        sensorIds,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        fields: availableMetrics.length > 0 ? ['timestamp', ...availableMetrics] : undefined,
      });
    } catch (error) {
      console.error('Export failed:', error);
      alert(t('export.exportFailed'));
    } finally {
      setExportLoading(false);
    }
  };

  // ── soil vs leaf metric groups ────────────────────────────────────────────

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Metric selector - only show metrics with data */}
          <div className="flex flex-wrap gap-2">
            {/* Soil metrics */}
            {availableMetrics.includes('temperature') && (
              <button
                onClick={() => setMetric('temperature')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'temperature'
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                }`}
              >
                Temperature
              </button>
            )}
            {availableMetrics.includes('soil_moisture') && (
              <button
                onClick={() => setMetric('soil_moisture')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'soil_moisture'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
                }`}
              >
                Soil Moisture
              </button>
            )}
            {availableMetrics.includes('electrical_conductivity') && (
              <button
                onClick={() => setMetric('electrical_conductivity')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'electrical_conductivity'
                    ? 'bg-purple-500 text-white border-purple-500'
                    : 'bg-white text-purple-500 border-purple-500 hover:bg-purple-50'
                }`}
              >
                EC
              </button>
            )}
            {availableMetrics.includes('leaf_temperature') && (
              <button
                onClick={() => setMetric('leaf_temperature')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'leaf_temperature'
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                }`}
              >
                Leaf Temp
              </button>
            )}
            {availableMetrics.includes('leaf_moisture') && (
              <button
                onClick={() => setMetric('leaf_moisture')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'leaf_moisture'
                    ? 'bg-green-500 text-white border-green-500'
                    : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
                }`}
              >
                Leaf Moisture
              </button>
            )}
            {availableMetrics.includes('battery_voltage') && (
              <button
                onClick={() => setMetric('battery_voltage')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'battery_voltage'
                    ? 'bg-yellow-500 text-white border-yellow-500'
                    : 'bg-white text-yellow-500 border-yellow-500 hover:bg-yellow-50'
                }`}
              >
                Battery
              </button>
            )}
            {availableMetrics.includes('ph_value') && (
              <button
                onClick={() => setMetric('ph_value')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'ph_value'
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-cyan-700 border-cyan-600 hover:bg-cyan-50'
                }`}
              >
                {t('wqsPh.ph')}
              </button>
            )}
            {availableMetrics.includes('par') && (
              <button
                onClick={() => setMetric('par')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'par'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-emerald-700 border-emerald-600 hover:bg-emerald-50'
                }`}
              >
                {t('par.par')}
              </button>
            )}
            {availableMetrics.includes('temp_ds18b20') && (
              <button
                onClick={() => setMetric('temp_ds18b20')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'temp_ds18b20'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white text-red-600 border-red-500 hover:bg-red-50'
                }`}
              >
                {t('wqsPh.waterTemperature')}
              </button>
            )}
            {availableMetrics.includes('ph_temperature') && (
              <button
                onClick={() => setMetric('ph_temperature')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'ph_temperature'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white text-red-600 border-red-500 hover:bg-red-50'
                }`}
              >
                {t('wqsPh.probeTemperature')}
              </button>
            )}
            {availableMetrics.includes('ec_temperature') && (
              <button
                onClick={() => setMetric('ec_temperature')}
                className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                  metric === 'ec_temperature'
                    ? 'bg-red-500 text-white border-red-500'
                    : 'bg-white text-red-600 border-red-500 hover:bg-red-50'
                }`}
              >
                {t('wqsEc.probeTemperature')}
              </button>
            )}
          </div>

          {/* Date range preset buttons and Export button */}
          <div className="flex flex-wrap gap-2 items-center">
            {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  dateRangePreset === preset
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === '1d' ? '24 Hours' : preset === '7d' ? '7 Days' : preset === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
            <div className="border-l border-gray-300 h-6 mx-1"></div>
            <button
              onClick={handleExport}
              disabled={exportLoading || sensorIds.length === 0}
              className="px-4 py-1 text-sm bg-green-600 text-white rounded border border-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Export comparison data to Excel"
            >
              {exportLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('export.exporting')}
                </>
              ) : (
                <>📊 {t('export.exportToExcel')}</>
              )}
            </button>
          </div>
        </div>

        {/* Custom date range */}
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleCustomDateApply}
            disabled={!startDate || !endDate}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply Custom Range
          </button>
          <button
            onClick={() => loadReadings()}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error messages */}
      {errorMessages.length > 0 && (
        <div className="mb-4 space-y-1">
          {errorMessages.map((msg, i) => (
            <p key={i} className="text-sm text-red-600">{msg}</p>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className="relative" style={{ height: '400px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div className="text-gray-600">Loading readings...</div>
          </div>
        ) : !hasData ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gray-500">No readings available for the selected time range.</div>
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {/* Reading count */}
      {!loading && hasData && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          Comparing {sensorIds.length} sensor{sensorIds.length !== 1 ? 's' : ''} · {totalReadings} total reading{totalReadings !== 1 ? 's' : ''}
          {dateRangePreset !== 'custom' && ` from the last ${dateRangePreset === '1d' ? '24 hours' : dateRangePreset === '7d' ? '7 days' : dateRangePreset === '30d' ? '30 days' : '90 days'}`}
        </div>
      )}
    </div>
  );
};

export default ComparisonChart;
